import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useNotificationDeliveryFeed } from '../hooks/useNotificationDeliveryFeed';
import {
  NOTIFICATION_DEBUG_CATALOG,
  NOTIFICATION_DEBUG_GROUPS,
  type NotificationDebugGroup,
} from '../utils/notificationDebugCatalog';
import {
  formatDeliveryChannel,
  formatDeliveryStatus,
  type DeliveryChannel,
} from '../utils/notificationDeliveryLog';
import './NotificationDebugPage.css';

const ALL_GROUPS = 'all' as const;
type GroupFilter = typeof ALL_GROUPS | NotificationDebugGroup;
type PageTab = 'map' | 'feed';

const ALL_CHANNELS = 'all' as const;
type ChannelFilter = typeof ALL_CHANNELS | DeliveryChannel;

function formatFeedTime(date: Date | null): string {
  if (!date) return '—';
  return date.toISOString().replace('T', ' ').slice(0, 19);
}

const NotificationDebugPage: React.FC = () => {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<PageTab>('feed');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>(ALL_GROUPS);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>(ALL_CHANNELS);
  const [query, setQuery] = useState('');

  const isAdmin = user?.role === 'admin';
  const { entries: feedEntries, loading: feedLoading, error: feedError } = useNotificationDeliveryFeed(
    !loading && isAdmin,
  );

  const mapRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NOTIFICATION_DEBUG_CATALOG.filter((row) => {
      if (groupFilter !== ALL_GROUPS && row.group !== groupFilter) return false;
      if (!q) return true;
      const haystack = [
        row.trigger,
        row.who,
        row.bellType,
        row.email,
        row.slack,
        row.prefs,
        row.source,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [groupFilter, query]);

  const feedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return feedEntries.filter((entry) => {
      if (channelFilter !== ALL_CHANNELS && entry.channel !== channelFilter) return false;
      if (!q) return true;
      const haystack = [
        entry.eventType,
        entry.eventLabel,
        entry.source,
        entry.recipientName,
        entry.recipientEmail,
        entry.triggeredByName,
        entry.inAppType,
        entry.title,
        entry.message,
        entry.channel,
        entry.status,
        entry.skipReason,
        entry.error,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [feedEntries, channelFilter, query]);

  if (loading) {
    return (
      <div className="notif-debug">
        <pre className="notif-debug-banner">loading…</pre>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const generated = new Date().toISOString().slice(0, 19).replace('T', ' ');

  return (
    <div className="notif-debug">
      <header className="notif-debug-header">
        <pre className="notif-debug-banner" aria-hidden>{`$ notification-debug --admin
LCTF CRM · ${tab === 'feed' ? `live feed · ${feedRows.length} rows` : `reference map · ${mapRows.length}/${NOTIFICATION_DEBUG_CATALOG.length} rows`} · ${generated} UTC`}</pre>

        <div className="notif-debug-tabs" role="tablist" aria-label="Debug views">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'feed'}
            className={`notif-debug-tab ${tab === 'feed' ? 'is-active' : ''}`}
            onClick={() => setTab('feed')}
          >
            live feed
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'map'}
            className={`notif-debug-tab ${tab === 'map' ? 'is-active' : ''}`}
            onClick={() => setTab('map')}
          >
            reference map
          </button>
        </div>

        <div className="notif-debug-controls">
          {tab === 'map' ? (
            <label className="notif-debug-filter">
              <span>group</span>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value as GroupFilter)}
                aria-label="Filter by group"
              >
                <option value={ALL_GROUPS}>all</option>
                {NOTIFICATION_DEBUG_GROUPS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label.toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="notif-debug-filter">
              <span>channel</span>
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value as ChannelFilter)}
                aria-label="Filter by channel"
              >
                <option value={ALL_CHANNELS}>all</option>
                <option value="bell">bell</option>
                <option value="email">email</option>
                <option value="slack_dm">slack:dm</option>
                <option value="slack_crm">slack:#crm</option>
              </select>
            </label>
          )}
          <label className="notif-debug-filter notif-debug-filter--search">
            <span>grep</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === 'feed' ? 'recipient, event, source…' : 'trigger, type, source…'}
              spellCheck={false}
              aria-label="Search"
            />
          </label>
        </div>
      </header>

      {tab === 'feed' ? (
        <>
          {feedError && (
            <p className="notif-debug-error" role="alert">
              {feedError}
            </p>
          )}
          <div className="notif-debug-table-wrap">
            <table className="notif-debug-table">
              <thead>
                <tr>
                  <th scope="col">time (utc)</th>
                  <th scope="col">event</th>
                  <th scope="col">triggered by</th>
                  <th scope="col">recipient</th>
                  <th scope="col">channel</th>
                  <th scope="col">status</th>
                  <th scope="col">type</th>
                  <th scope="col">source</th>
                </tr>
              </thead>
              <tbody>
                {feedLoading ? (
                  <tr>
                    <td colSpan={8} className="notif-debug-empty">
                      loading feed…
                    </td>
                  </tr>
                ) : feedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="notif-debug-empty">
                      no deliveries yet — trigger a notification after deploying functions
                    </td>
                  </tr>
                ) : (
                  feedRows.map((entry) => (
                    <tr key={entry.id}>
                      <td className="notif-debug-time">{formatFeedTime(entry.createdAt)}</td>
                      <td className="notif-debug-trigger">
                        <span>{entry.eventLabel}</span>
                        {entry.title && entry.title !== entry.eventLabel && (
                          <span className="notif-debug-sub">{entry.title}</span>
                        )}
                      </td>
                      <td>{entry.triggeredByName || '—'}</td>
                      <td>
                        {entry.recipientName || '—'}
                        {entry.recipientEmail ? (
                          <span className="notif-debug-sub">{entry.recipientEmail}</span>
                        ) : null}
                      </td>
                      <td className={`notif-debug-channel notif-debug-channel--${entry.channel}`}>
                        {formatDeliveryChannel(entry.channel)}
                      </td>
                      <td className={`notif-debug-status notif-debug-status--${entry.status}`}>
                        {formatDeliveryStatus(entry)}
                      </td>
                      <td className="notif-debug-type">{entry.inAppType || entry.eventType || '—'}</td>
                      <td className="notif-debug-source">{entry.source}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <footer className="notif-debug-footer">
            <p>
              Live feed — last {feedEntries.length} delivery rows (bell, email, Slack DM, #CRM). Updates in real time.
              Deploy Cloud Functions + Firestore rules for new events to appear here.
            </p>
          </footer>
        </>
      ) : (
        <>
          <div className="notif-debug-table-wrap">
            <table className="notif-debug-table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">trigger</th>
                  <th scope="col">who</th>
                  <th scope="col">bell type</th>
                  <th scope="col">email</th>
                  <th scope="col">slack</th>
                  <th scope="col">prefs</th>
                  <th scope="col">source</th>
                </tr>
              </thead>
              <tbody>
                {mapRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="notif-debug-empty">
                      no matches
                    </td>
                  </tr>
                ) : (
                  mapRows.map((row, index) => (
                    <tr key={row.id}>
                      <td className="notif-debug-num">{String(index + 1).padStart(2, '0')}</td>
                      <td className="notif-debug-trigger">{row.trigger}</td>
                      <td>{row.who}</td>
                      <td className="notif-debug-type">{row.bellType}</td>
                      <td>{row.email}</td>
                      <td>{row.slack}</td>
                      <td className="notif-debug-prefs">{row.prefs}</td>
                      <td className="notif-debug-source">{row.source}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <footer className="notif-debug-footer">
            <p>
              Reference map — intended delivery paths. User prefs gate email and task/ops toggles.
              Slack DMs are off. #CRM posts milestones only.
            </p>
          </footer>
        </>
      )}
    </div>
  );
};

export default NotificationDebugPage;
